import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './App.css';
import SimpleChatView from './components/SimpleChatView';
import SplitView from './components/SplitView';
import SiteAgentView from './components/SiteAgentView';
import {
  getBrands,
  getConversations,
  createConversation,
  sendMessage as sendMessageAPI,
  sendMessageStream,
  getSessionInfo,
  logoutSession,
  buildAuthLoginUrl,
  deleteConversation as deleteConversationAPI,
  updateConversation as updateConversationAPI,
  getAuthBaseUrl,
  getAuthProvidersInfo,
  requestMagicLink,
  getCsrfToken,
  branchConversation,
  getConversationTree,
} from './api/api';
import AuthPortal from './components/AuthPortal';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import NodeRiverSpinner from './components/NodeRiverSpinner';
import { buildBrandVisualTheme, DEFAULT_BRAND_VISUAL_THEME } from './lib/brandVisualTheme';
import { applyAlpha } from './lib/colorUtils';
import MarketResearchDashboard from './components/MarketResearchDashboard';
import BranchTreeModal from './components/BranchTreeModal';

const PENDING_MESSAGE_KEY = 'brandon-ui-pending-message';
const VIEW_MODE_STORAGE_KEY = 'brandon-ui-view-mode';
const ACTIVE_CONVERSATION_STORAGE_KEY = 'brandon-ui-active-conversation-id';
const ACTIVE_BRAND_STORAGE_KEY = 'brandon-ui-active-brand-id';
const ENVIRONMENT_STORAGE_KEY = 'brandon-ui-environment';

const getStoredViewMode = () => {
  if (typeof window === 'undefined') return 'simple';
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  if (stored === 'split' || stored === 'site-agent') return stored;
  return 'simple';
};

const getStoredConversationId = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_CONVERSATION_STORAGE_KEY);
};

const normalizeEnvironmentKey = (value) => {
  if (!value) return 'production';
  const normalized = value.trim().toLowerCase();
  const allowed = ['production', 'staging', 'preview', 'development'];
  return allowed.includes(normalized) ? normalized : 'production';
};

const getStoredEnvironment = () => {
  if (typeof window === 'undefined') return 'production';
  const stored = window.localStorage.getItem(ENVIRONMENT_STORAGE_KEY);
  return normalizeEnvironmentKey(stored ?? 'production');
};

const getStoredBrandId = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY);
};

function AppContent() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [activeBrand, setActiveBrand] = useState(null);
  const [brandList, setBrandList] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(() => getStoredBrandId());
  const [availableEnvironments, setAvailableEnvironments] = useState([]);
  const [activeEnvironment, setActiveEnvironment] = useState(() => getStoredEnvironment());
  const [brandProfileSpec, setBrandProfileSpec] = useState(null);
  const [brandApplicationSlice, setBrandApplicationSlice] = useState(null);
  const [brandContextPacks, setBrandContextPacks] = useState(null);
  const [brandVisualSpec, setBrandVisualSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => getStoredViewMode()); // 'simple' or 'split'
  const [authStatus, setAuthStatus] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated' | 'error'
  const [sessionUser, setSessionUser] = useState(null);
  const [authPortalOpen, setAuthPortalOpen] = useState(false);
  const [authPortalMode, setAuthPortalMode] = useState('login');
  const [authProviders, setAuthProviders] = useState(null);
  const [magicLinkState, setMagicLinkState] = useState({ status: 'idle', email: null, error: null });
  const [magicLinkPending, setMagicLinkPending] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [oauthPendingProvider, setOauthPendingProvider] = useState(null);
  const [isResearchDashboardOpen, setIsResearchDashboardOpen] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null); // { conversationId, content } for streaming assistant message
  
  // Branch state management
  const [branchTreeModalOpen, setBranchTreeModalOpen] = useState(false);
  const [branchTree, setBranchTree] = useState(null);
  const [branchTreeLoading, setBranchTreeLoading] = useState(false);
  const [branchIndex, setBranchIndex] = useState(1);
  const [totalBranches, setTotalBranches] = useState(1);
  
  const portalBaseUrl = useMemo(() => getAuthBaseUrl(), []);
  const { toast } = useToast();
  const statusRef = useRef(authStatus);
  const pendingMessageRef = useRef(null);
  const sendMessageRef = useRef(null);
  const { resolvedTheme } = useTheme();
  const brandVisualTheme = useMemo(
    () => buildBrandVisualTheme(brandVisualSpec, brandProfileSpec),
    [brandVisualSpec, brandProfileSpec]
  );
  const [brandSpinnerUnlocked, setBrandSpinnerUnlocked] = useState(false);
  const isBrandTheme = resolvedTheme === 'noderiver';
  const isLightTheme = resolvedTheme === 'light';
  const isDarkTheme = resolvedTheme === 'dark';
  const spinnerStyle = useMemo(() => {
    const palette = brandVisualTheme?.palette ?? {};
    const primary = palette.primary ?? '#1E8997';
    const dark = palette.dark ?? '#031a24';
    return {
      '--brand-spinner-bg': `radial-gradient(circle at 30% 30%, ${applyAlpha(primary, 0.2)}, ${applyAlpha(dark, 0.08)})`,
      '--brand-spinner-shadow': `0 18px 45px ${applyAlpha(dark, 0.25)}, inset 0 0 28px ${applyAlpha(primary, 0.35)}`,
      '--brand-spinner-border': applyAlpha(primary, 0.3),
      '--brand-spinner-border-top': primary,
      '--brand-spinner-glow': applyAlpha(dark, 0.35),
      '--brand-spinner-img-shadow': `0 12px 30px ${applyAlpha(dark, 0.35)}`,
    };
  }, [brandVisualTheme]);

  const activeThemeLogo = useMemo(
    () => brandVisualTheme?.logosByMode?.[resolvedTheme] ?? brandVisualTheme?.logo,
    [brandVisualTheme, resolvedTheme]
  );

  const hasCustomBrandLogo = useMemo(() => {
    const brandLogo = activeThemeLogo?.uri;
    if (!brandVisualSpec || !brandLogo) return false;
    return brandLogo !== DEFAULT_BRAND_VISUAL_THEME.logo.uri;
  }, [brandVisualTheme, brandVisualSpec, activeThemeLogo]);

  useEffect(() => {
    const stillLoading = authStatus === 'loading' || loading;
    if (!stillLoading || !hasCustomBrandLogo) {
      if (brandSpinnerUnlocked) {
        setBrandSpinnerUnlocked(false);
      }
      return;
    }
    if (brandSpinnerUnlocked) return;
    const timer = setTimeout(() => setBrandSpinnerUnlocked(true), 220);
    return () => clearTimeout(timer);
  }, [authStatus, loading, hasCustomBrandLogo, brandSpinnerUnlocked]);

  const refreshSession = useCallback(async () => {
    try {
      const result = await getSessionInfo();
      if (result?.authenticated) {
        setAuthStatus('authenticated');
        setSessionUser(result.user);
        return true;
      }
      setAuthStatus('unauthenticated');
      setSessionUser(null);
      return false;
    } catch (error) {
      console.error('Error checking session:', error);
      setAuthStatus('error');
      setSessionUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authStatus !== 'authenticated') return;
    if (!activeConversation?.id) return;
    window.localStorage.setItem(ACTIVE_CONVERSATION_STORAGE_KEY, String(activeConversation.id));
  }, [activeConversation, authStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authStatus === 'unauthenticated' || authStatus === 'error') {
      window.localStorage.removeItem(ACTIVE_CONVERSATION_STORAGE_KEY);
    }
  }, [authStatus]);

  useEffect(() => {
    let cancelled = false;
    const loadProviders = async () => {
      try {
        const info = await getAuthProvidersInfo();
        if (!cancelled) {
          setAuthProviders(info);
        }
      } catch (error) {
        console.error('[BrandOnUI] Failed to load auth provider metadata', error);
      }
    };
    loadProviders();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(PENDING_MESSAGE_KEY);
    if (stored) {
      try {
        pendingMessageRef.current = JSON.parse(stored);
      } catch {
        window.localStorage.removeItem(PENDING_MESSAGE_KEY);
        pendingMessageRef.current = null;
      }
    }
  }, []);

  const resetMagicLinkState = useCallback(() => {
    setMagicLinkState({ status: 'idle', email: null, error: null });
  }, []);

  const openAuthPortal = useCallback(
    (mode = 'login') => {
      setAuthPortalMode(mode);
      setAuthPortalOpen(true);
      resetMagicLinkState();
    },
    [resetMagicLinkState],
  );

  const closeAuthPortal = useCallback(() => {
    setAuthPortalOpen(false);
    resetMagicLinkState();
  }, [resetMagicLinkState]);

  const applyBrandState = useCallback(
    async (brand, environmentKey) => {
      if (!brand) {
        setActiveBrand(null);
        setActiveBrandId(null);
        setBrandProfileSpec(null);
        setBrandApplicationSlice(null);
        setBrandContextPacks(null);
        setBrandVisualSpec(null);
        setAvailableEnvironments([]);
        setConversations([]);
        setActiveConversation(null);
        const normalizedEnv = normalizeEnvironmentKey(environmentKey ?? activeEnvironment);
        setActiveEnvironment(normalizedEnv);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(ENVIRONMENT_STORAGE_KEY, normalizedEnv);
          window.localStorage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
        }
        return;
      }

      setActiveBrand(brand);
      setActiveBrandId(brand.id);
      const profile = brand.brand_profile_spec ?? brand.profileSpec ?? null;
      setBrandProfileSpec(profile);
      const applicationSpecs = brand.application_specs ?? {};
      setBrandApplicationSlice(applicationSpecs.brandon ?? null);
      setBrandContextPacks(applicationSpecs.context_packs ?? null);
      setBrandVisualSpec(applicationSpecs.visual ?? null);
      setAvailableEnvironments(applicationSpecs.available_environments ?? []);

      const resolvedEnv = normalizeEnvironmentKey(
        applicationSpecs.environment ?? environmentKey ?? activeEnvironment
      );
      setActiveEnvironment(resolvedEnv);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, String(brand.id));
        window.localStorage.setItem(ENVIRONMENT_STORAGE_KEY, resolvedEnv);
      }

      const convs = await getConversations(brand.id);
      setConversations(convs);

      if (viewMode === 'split') {
        if (convs.length === 0) {
          setActiveConversation(null);
        } else {
          const storedConversationId = getStoredConversationId();
          const matchingConversation = storedConversationId
            ? convs.find((conv) => String(conv.id) === storedConversationId)
            : null;
          const nextConversation = matchingConversation ?? convs[0];
          setActiveConversation(nextConversation);

          if (matchingConversation?.id) {
            try {
              const { getConversation } = await import('./api/api');
              const fullConv = await getConversation(matchingConversation.id);
              setActiveConversation((prev) => {
                if (!prev || prev.id !== matchingConversation.id) {
                  return prev;
                }
                return fullConv;
              });
            } catch (restoreError) {
              console.error('[BrandOnUI] Failed to restore active conversation', restoreError);
            }
          }
        }
      } else {
        setActiveConversation(null);
      }
    },
    [activeEnvironment, viewMode]
  );

  const fetchBrands = useCallback(
    async ({ environmentKey, brandIdOverride } = {}) => {
      if (authStatus !== 'authenticated') {
        setLoading(false);
        return;
      }
      const envKey = normalizeEnvironmentKey(
        environmentKey ?? activeEnvironment ?? getStoredEnvironment()
      );
      try {
        setLoading(true);
        console.debug("[BrandOnUI] Loading brands + conversations for environment", envKey);
        const brands = await getBrands({ environment: envKey });
        setBrandList(brands);
        if (brands.length === 0) {
          await applyBrandState(null, envKey);
          return;
        }
        const storedBrandId =
          typeof window === 'undefined' ? null : window.localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY);
        const preferredId = brandIdOverride ?? activeBrandId ?? storedBrandId;
        const nextBrand =
          (preferredId &&
            brands.find((brand) => String(brand.id) === String(preferredId))) ??
          brands[0];
        await applyBrandState(nextBrand, envKey);
      } catch (error) {
        console.error('Error loading data:', error);
        if (error?.response?.status === 401) {
          setAuthStatus('unauthenticated');
          setSessionUser(null);
        }
        toast({
          title: 'Error',
          description: 'Failed to load data. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [activeEnvironment, activeBrandId, applyBrandState, authStatus, toast]
  );

  const handleBrandChange = useCallback(
    (brandId) => {
      if (!brandId) return;
      fetchBrands({ environmentKey: activeEnvironment, brandIdOverride: brandId });
    },
    [activeEnvironment, fetchBrands]
  );

  const handleEnvironmentChange = useCallback(
    (environmentKey) => {
      const normalized = normalizeEnvironmentKey(environmentKey);
      fetchBrands({
        environmentKey: normalized,
        brandIdOverride: activeBrand?.id ?? getStoredBrandId(),
      });
    },
    [activeBrand, fetchBrands]
  );

  useEffect(() => {
    if (authStatus === 'authenticated' && statusRef.current !== 'authenticated') {
      fetchBrands({
        environmentKey: activeEnvironment ?? getStoredEnvironment(),
        brandIdOverride: activeBrandId ?? getStoredBrandId(),
      });
    } else if (authStatus === 'unauthenticated' || authStatus === 'error') {
      setLoading(false);
      setBrandList([]);
      setActiveBrand(null);
      setActiveBrandId(null);
      setBrandProfileSpec(null);
      setBrandApplicationSlice(null);
      setBrandContextPacks(null);
      setBrandVisualSpec(null);
      setConversations([]);
      setActiveConversation(null);
      setAvailableEnvironments([]);
      setViewMode('simple');
    }
    statusRef.current = authStatus;
  }, [authStatus, activeEnvironment, activeBrandId, fetchBrands]);

  useEffect(() => {
    if (authPortalOpen && authPortalMode === 'login' && authStatus === 'authenticated') {
      closeAuthPortal();
    }
  }, [authPortalOpen, authPortalMode, authStatus, closeAuthPortal]);

  const promptSignin = useCallback(
    (message = 'Sign in to continue.', shouldOpenPortal = true) => {
      toast({
        title: 'Sign in required',
        description: message,
      });
      if (shouldOpenPortal) {
        openAuthPortal('login');
      }
    },
    [toast, openAuthPortal]
  );

  const redirectToClassicLogin = useCallback(() => {
    if (typeof window === 'undefined') return;
    const callback = window.location.href;
    window.location.href = buildAuthLoginUrl(callback);
  }, []);

  const handleLogin = useCallback(() => {
    openAuthPortal('login');
  }, [openAuthPortal]);

  const performLogout = useCallback(async () => {
    try {
      await logoutSession();
      toast({
        title: 'Signed out',
        description: 'You have been signed out of BrandOn.',
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setViewMode('simple');
      setActiveConversation(null);
      setConversations([]);
      setActiveBrand(null);
      setBrandList([]);
      setActiveBrandId(null);
      setBrandProfileSpec(null);
      setBrandApplicationSlice(null);
      setBrandContextPacks(null);
      setAvailableEnvironments([]);
      setActiveEnvironment('production');
      pendingMessageRef.current = null;
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(PENDING_MESSAGE_KEY);
        window.localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
        window.localStorage.removeItem(ACTIVE_CONVERSATION_STORAGE_KEY);
        window.localStorage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
        window.localStorage.removeItem(ENVIRONMENT_STORAGE_KEY);
      }
      await refreshSession();
    }
  }, [refreshSession, toast]);

  const handleLogoutRequest = useCallback(() => {
    if (authStatus !== 'authenticated') {
      openAuthPortal('login');
      return;
    }
    openAuthPortal('logout');
  }, [authStatus, openAuthPortal]);

  const handleMagicLinkRequest = useCallback(
    async (emailValue) => {
      const normalized = emailValue?.trim();
      if (!normalized) return;
      setMagicLinkPending(true);
      setMagicLinkState({ status: 'pending', email: null, error: null });
      try {
        const callbackUrl =
          typeof window !== 'undefined' ? window.location.href : portalBaseUrl;
        const response = await requestMagicLink({ email: normalized, callbackUrl });
        const confirmedEmail = response?.email ?? normalized;
        setMagicLinkState({ status: 'success', email: confirmedEmail, error: null });
        toast({
          title: 'Magic link sent',
          description: `Check ${confirmedEmail} for your secure link.`,
        });
      } catch (error) {
        const message =
          error?.response?.data?.error ??
          error?.message ??
          'We couldn’t send that link. Please try again.';
        setMagicLinkState({ status: 'error', email: null, error: message });
        toast({
          title: 'Magic link failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setMagicLinkPending(false);
      }
    },
    [portalBaseUrl, toast],
  );

  const handleProviderSelect = useCallback(
    async (providerId) => {
      if (!providerId || typeof window === 'undefined') return;
      setOauthPendingProvider(providerId);
      const base = (portalBaseUrl ?? '').replace(/\/$/, '');
      const callbackUrl = window.location.href;

      try {
        const csrfToken = await getCsrfToken();
        if (csrfToken) {
          const form = document.createElement('form');
          form.method = 'post';
          form.action = `${base}/api/auth/signin/${providerId}`;

          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'csrfToken';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);

          const callbackInput = document.createElement('input');
          callbackInput.type = 'hidden';
          callbackInput.name = 'callbackUrl';
          callbackInput.value = callbackUrl;
          form.appendChild(callbackInput);

          document.body.appendChild(form);
          form.submit();
          return;
        }
      } catch (error) {
        console.warn('[BrandOnUI] Failed to fetch CSRF token, falling back to GET', error);
      }

      const encodedCallback = encodeURIComponent(callbackUrl);
      window.location.href = `${base}/api/auth/signin/${providerId}?callbackUrl=${encodedCallback}`;
    },
    [portalBaseUrl],
  );

  const confirmLogout = useCallback(async () => {
    setLogoutPending(true);
    try {
      await performLogout();
      closeAuthPortal();
    } finally {
      setLogoutPending(false);
    }
  }, [performLogout, closeAuthPortal]);

  const accountLabel = useMemo(() => {
    const raw =
      sessionUser?.accountType ??
      sessionUser?.plan ??
      activeBrand?.account_type ??
      activeBrand?.plan ??
      null;
    if (!raw) return 'Personal account';
    const value = typeof raw === 'string' ? raw : String(raw);
    const trimmed = value.trim();
    if (!trimmed) return 'Personal account';
    const normalized = trimmed.toLowerCase().includes('account') ? trimmed : `${trimmed} account`;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }, [sessionUser, activeBrand]);

  const handleNewConversation = async () => {
    if (authStatus !== 'authenticated') {
      promptSignin();
      return null;
    }
    if (!activeBrand) {
      toast({
        title: 'No brand selected',
        description: 'Create a brand first to start conversations.',
      });
      return null;
    }
    
    try {
      console.debug("[BrandOnUI] Creating new conversation for brand", activeBrand.id);
      const newConv = await createConversation(activeBrand.id);
      
      setConversations((prev) => [newConv, ...prev]);
      setActiveConversation(newConv);
      
      // Switch to split view when creating new conversation from simple view
      if (viewMode === 'simple') {
        setViewMode('split');
      }
      
      toast({
        title: 'New conversation started',
        description: 'How can I help you today?'
      });
      return newConv;
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to create conversation.',
        variant: 'destructive'
      });
      return null;
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!conversationId) return;
    try {
      await deleteConversationAPI(conversationId);
      setConversations((prev) => {
        const updated = prev.filter((conv) => conv.id !== conversationId);
        if (activeConversation?.id === conversationId) {
          const nextConversation = updated[0] ?? null;
          setActiveConversation(nextConversation);
          if (!nextConversation) {
            setViewMode('simple');
          }
        }
        return updated;
      });
      toast({
        title: 'Conversation deleted',
        description: 'The conversation has been removed.',
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete conversation.',
        variant: 'destructive',
      });
    }
  };

  const handleRenameConversation = async (conversationId, newTitle) => {
    const normalized = newTitle?.trim();
    if (!normalized) {
      toast({
        title: 'Title required',
        description: 'Please provide a name for the conversation.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const updated = await updateConversationAPI(conversationId, { title: normalized });
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, title: updated.title ?? normalized } : conv
        )
      );
      setActiveConversation((prev) =>
        prev && prev.id === conversationId ? { ...prev, title: updated.title ?? normalized } : prev
      );
      toast({
        title: 'Conversation renamed',
        description: 'The new title has been saved.',
      });
    } catch (error) {
      console.error('Error renaming conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to rename conversation.',
        variant: 'destructive',
      });
    }
  };

  // Branch handlers
  const handleBranchMessage = useCallback(async (message) => {
    if (authStatus !== 'authenticated') {
      promptSignin('Sign in to create conversation branches.');
      return;
    }
    
    if (!activeConversation?.id || !message?.id) {
      toast({
        title: 'Cannot branch',
        description: 'Please select a conversation and message to branch from.',
        variant: 'destructive',
      });
      return;
    }

    try {
      console.debug('[BrandOnUI] Creating branch from message', {
        conversationId: activeConversation.id,
        messageId: message.id,
      });
      
      const branchedConv = await branchConversation(activeConversation.id, message.id);
      
      // Add the new branched conversation to the list
      setConversations((prev) => [branchedConv, ...prev]);
      
      // Switch to the new branch
      setActiveConversation(branchedConv);
      
      // Update branch info
      await refreshBranchInfo(branchedConv.id);
      
      toast({
        title: 'Branch created',
        description: 'You can now continue from this point in a new direction.',
      });
    } catch (error) {
      console.error('Error creating branch:', error);
      toast({
        title: 'Error',
        description: 'Failed to create branch. Please try again.',
        variant: 'destructive',
      });
    }
  }, [authStatus, activeConversation, promptSignin, toast]);

  const refreshBranchInfo = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const tree = await getConversationTree(conversationId);
      setBranchTree(tree);
      
      // Calculate branch index and total
      const nodes = tree?.nodes ?? [];
      const total = nodes.length;
      const currentIndex = nodes.findIndex(n => String(n.id) === String(conversationId)) + 1;
      
      setTotalBranches(total);
      setBranchIndex(currentIndex > 0 ? currentIndex : 1);
    } catch (error) {
      console.debug('[BrandOnUI] Could not fetch branch tree:', error);
      // Not an error - conversation might not have branches
      setBranchTree(null);
      setTotalBranches(1);
      setBranchIndex(1);
    }
  }, []);

  const handleOpenBranchTree = useCallback(async () => {
    if (!activeConversation?.id) return;
    
    setBranchTreeModalOpen(true);
    setBranchTreeLoading(true);
    
    try {
      const tree = await getConversationTree(activeConversation.id);
      setBranchTree(tree);
      
      // Update branch counts
      const nodes = tree?.nodes ?? [];
      setTotalBranches(nodes.length);
      const currentIndex = nodes.findIndex(n => String(n.id) === String(activeConversation.id)) + 1;
      setBranchIndex(currentIndex > 0 ? currentIndex : 1);
    } catch (error) {
      console.error('Error loading branch tree:', error);
      toast({
        title: 'Error',
        description: 'Failed to load branch tree.',
        variant: 'destructive',
      });
    } finally {
      setBranchTreeLoading(false);
    }
  }, [activeConversation, toast]);

  const handleCloseBranchTree = useCallback(() => {
    setBranchTreeModalOpen(false);
  }, []);

  const handleSelectBranch = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const { getConversation } = await import('./api/api');
      const fullConv = await getConversation(conversationId);
      setActiveConversation(fullConv);
      
      // Update branch info for the new conversation
      await refreshBranchInfo(conversationId);
      
      // Update conversations list if needed
      setConversations((prev) => {
        const exists = prev.some(c => String(c.id) === String(conversationId));
        if (!exists) {
          return [fullConv, ...prev];
        }
        return prev;
      });
      
      setBranchTreeModalOpen(false);
    } catch (error) {
      console.error('Error switching to branch:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch to selected branch.',
        variant: 'destructive',
      });
    }
  }, [refreshBranchInfo, toast]);

  // Refresh branch info when active conversation changes
  useEffect(() => {
    if (activeConversation?.id) {
      refreshBranchInfo(activeConversation.id);
    } else {
      setBranchTree(null);
      setBranchIndex(1);
      setTotalBranches(1);
    }
  }, [activeConversation?.id, refreshBranchInfo]);

  const handleSendMessage = async (messageContent, switchToSplit = false) => {
    if (authStatus !== 'authenticated') {
      const pendingPayload = { content: messageContent, switchToSplit };
      pendingMessageRef.current = pendingPayload;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(PENDING_MESSAGE_KEY, JSON.stringify(pendingPayload));
      }
      promptSignin('Sign in to continue.');
      return;
    }
    let conversation = activeConversation;
    if (!conversation) {
      conversation = await handleNewConversation();
    }

    if (!conversation) return;
    console.debug("[BrandOnUI] Sending message with streaming", {
      conversationId: conversation.id,
      previewMessage: messageContent.slice(0, 80),
    });
    
    const conversationId = conversation.id;
    
    // Switch to split view if requested
    if (switchToSplit) {
      setViewMode('split');
    }

    // Add optimistic user message immediately so it appears before "thinking" indicator
    const tempUserMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
    };
    setActiveConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: [...(prev.messages || []), tempUserMessage],
      };
    });

    // Initialize streaming state
    setStreamingMessage({ conversationId, content: '' });
    
    try {
      await sendMessageStream(conversationId, messageContent, {
        onUserMessage: (userMessage) => {
          // Add real user message from server
          setActiveConversation((prev) => {
            if (!prev || prev.id !== conversationId) return prev;
            const base = prev.messages || [];
            // Filter out any temp messages
            const filtered = base.filter(m => !String(m.id).startsWith('temp-'));
            return {
              ...prev,
              messages: [...filtered, userMessage]
            };
          });
        },
        onToken: (token) => {
          // Append token to streaming message
          setStreamingMessage((prev) => {
            if (!prev || prev.conversationId !== conversationId) return prev;
            return { ...prev, content: prev.content + token };
          });
        },
        onDone: (assistantMessage, evaluation) => {
          // Clear streaming state and add final message
          setStreamingMessage(null);
          
          const nextTitle =
            (conversation.messages?.length ?? 0) > 0
              ? conversation.title
              : messageContent.length > 50
                ? `${messageContent.substring(0, 50)}...`
                : messageContent;
          
          setActiveConversation((prev) => {
            if (!prev || prev.id !== conversationId) return prev;
            const filtered = (prev.messages || []).filter(m => !String(m.id).startsWith('temp-'));
            return {
              ...prev,
              messages: [...filtered, assistantMessage],
              title: nextTitle
            };
          });
          
          // Update conversations list with new title
          setConversations((prev) =>
            prev.map(c =>
              c.id === conversationId ? { ...c, title: nextTitle } : c
            )
          );
        },
        onError: (error) => {
          console.error('Streaming error:', error);
          setStreamingMessage(null);
          toast({
            title: 'Error',
            description: 'Failed to send message. Please try again.',
            variant: 'destructive'
          });
        }
      });
      
    } catch (error) {
      console.error('Error sending message:', error);
      setStreamingMessage(null);
      
      if (error?.response?.status === 401) {
        promptSignin('Your session ended. Please sign in again.');
        await refreshSession();
      }
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
      
      // Remove any temp messages on error
      setActiveConversation((prev) => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).filter(m => !String(m.id).startsWith('temp-'))
        };
      });
    }
  };

  useEffect(() => {
    sendMessageRef.current = handleSendMessage;
  });

  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    if (!pendingMessageRef.current) return;
    const payload = pendingMessageRef.current;
    pendingMessageRef.current = null;
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(PENDING_MESSAGE_KEY);
    }
    sendMessageRef.current?.(payload.content, payload.switchToSplit ?? false);
  }, [authStatus]);

  const handleSelectConversation = async (conversation) => {
    if (authStatus !== 'authenticated') {
      promptSignin('Sign in to view your saved conversations.');
      return;
    }
    try {
      // Fetch full conversation with messages
      const { getConversation } = await import('./api/api');
      const fullConv = await getConversation(conversation.id);
      setActiveConversation(fullConv);
      
      // Switch to split view when selecting a conversation
      if (viewMode === 'simple') {
        setViewMode('split');
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      if (error?.response?.status === 401) {
        promptSignin('Your session ended. Please sign in again.');
        await refreshSession();
        return;
      }
      setActiveConversation(conversation);
    }
  };

  const handleSwitchView = () => {
    if (viewMode === 'simple') {
      if (authStatus !== 'authenticated') {
        promptSignin('Sign in to use split view.');
        return;
      }
      setViewMode('split');
      if (!activeConversation && conversations.length > 0) {
        const storedConversationId = getStoredConversationId();
        if (storedConversationId) {
          const storedConversation = conversations.find(
            (conv) => String(conv.id) === storedConversationId
          );
          if (storedConversation) {
            setActiveConversation(storedConversation);
            return;
          }
        }
        setActiveConversation(conversations[0]);
      }
      return;
    }
    setViewMode('simple');
    setActiveConversation(null);
  };

  if (authStatus === 'loading' || loading) {
    const loadingBackground = (() => {
      if (isBrandTheme && brandVisualTheme?.gradients?.hero) {
        return { background: brandVisualTheme.gradients.hero };
      }
      if (isLightTheme) {
        return { backgroundColor: '#ffffff' };
      }
      if (isDarkTheme) {
        return { backgroundColor: '#0b1220' };
      }
      return { backgroundColor: brandVisualTheme?.modes?.light?.background ?? '#f3f4f6' };
    })();
    const loadingTextColor = isBrandTheme
      ? 'rgba(255,255,255,0.92)'
      : isLightTheme
        ? '#4b5563'
        : '#e2e8f0';
    return (
      <div className="h-screen flex items-center justify-center" style={loadingBackground}>
        <div className="text-center">
          <NodeRiverSpinner
            size={120}
            variant="dark"
            label={authStatus === 'loading' ? 'Checking your session' : 'Loading BrandOn Agent'}
            className="mx-auto mb-6"
            style={brandSpinnerUnlocked ? spinnerStyle : undefined}
            logoSrc={brandSpinnerUnlocked && hasCustomBrandLogo ? activeThemeLogo?.uri : undefined}
          />
          <p style={{ color: loadingTextColor }}>
            {authStatus === 'loading' ? 'Checking your session...' : 'Loading BrandOn Agent...'}
          </p>
        </div>
      </div>
    );
  }

  const handleUpdateUser = (updatedUser) => {
    setSessionUser(updatedUser);
  };

  const handleOpenSiteAgent = () => {
    if (authStatus !== 'authenticated') {
      promptSignin('Sign in to use Site Agent.');
      return;
    }
    setViewMode('site-agent');
  };

  const showSplitView = authStatus === 'authenticated' && viewMode === 'split';
  const showSiteAgent = authStatus === 'authenticated' && viewMode === 'site-agent';
  const providerConfig = authProviders?.providers ?? null;

  return (
    <>
      {showSiteAgent ? (
        <SiteAgentView
          brandId={activeBrandId}
          brandSlug={activeBrand?.slug}
          brandName={activeBrand?.name}
          brandVisualTheme={brandVisualTheme}
          onBack={() => setViewMode('split')}
        />
      ) : showSplitView ? (
        <SplitView
          conversations={conversations}
          activeConversation={activeConversation}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onSendMessage={handleSendMessage}
          onDeleteConversation={handleDeleteConversation}
          onRenameConversation={handleRenameConversation}
          brandMeta={activeBrand}
          brandProfileSpec={brandProfileSpec}
          brandApplicationSlice={brandApplicationSlice}
          brandContextPacks={brandContextPacks}
          brandVisualTheme={brandVisualTheme}
          onSwitchView={handleSwitchView}
          sessionUser={sessionUser}
          onUpdateUser={handleUpdateUser}
          onLogin={handleLogin}
          onLogout={handleLogoutRequest}
          authStatus={authStatus}
          accountLabel={accountLabel}
          portalBaseUrl={portalBaseUrl}
          brands={brandList}
          activeBrandId={activeBrandId}
          onBrandChange={handleBrandChange}
          availableEnvironments={availableEnvironments}
          activeEnvironment={activeEnvironment}
          onEnvironmentChange={handleEnvironmentChange}
          onOpenResearch={() => setIsResearchDashboardOpen(true)}
          onOpenSiteAgent={handleOpenSiteAgent}
          streamingMessage={streamingMessage}
          onBranchMessage={handleBranchMessage}
          onOpenBranchTree={handleOpenBranchTree}
          branchIndex={branchIndex}
          totalBranches={totalBranches}
        />
      ) : (
        <SimpleChatView
          conversation={activeConversation}
          onSendMessage={handleSendMessage}
          onSwitchView={handleSwitchView}
          brandMeta={activeBrand}
          brandProfileSpec={brandProfileSpec}
          brandApplicationSlice={brandApplicationSlice}
          brandContextPacks={brandContextPacks}
          brandVisualTheme={brandVisualTheme}
          canSendMessage={authStatus === 'authenticated'}
          onRequireAuth={handleLogin}
          authStatus={authStatus}
          sessionUser={sessionUser}
          accountLabel={accountLabel}
          onLogin={handleLogin}
          onLogout={handleLogoutRequest}
          brands={brandList}
          activeBrandId={activeBrandId}
          onBrandChange={handleBrandChange}
          availableEnvironments={availableEnvironments}
          activeEnvironment={activeEnvironment}
          onEnvironmentChange={handleEnvironmentChange}
          onUpdateUser={handleUpdateUser}
          streamingMessage={streamingMessage}
          onBranchMessage={handleBranchMessage}
          onOpenBranchTree={handleOpenBranchTree}
          branchIndex={branchIndex}
          totalBranches={totalBranches}
        />
      )}
      <Toaster />
      <AuthPortal
        open={authPortalOpen}
        mode={authPortalMode}
        onClose={closeAuthPortal}
        providers={providerConfig}
        onMagicLinkSubmit={handleMagicLinkRequest}
        magicLinkState={magicLinkState}
        magicLinkPending={magicLinkPending}
        onProviderSelect={handleProviderSelect}
        onOpenClassicLogin={redirectToClassicLogin}
        oauthPendingProvider={oauthPendingProvider}
        logoutPending={logoutPending}
        onConfirmLogout={confirmLogout}
        sessionUser={sessionUser}
      />
      <MarketResearchDashboard
        open={isResearchDashboardOpen}
        onClose={() => setIsResearchDashboardOpen(false)}
        brandId={activeBrandId}
        brandVisualTheme={brandVisualTheme}
      />
      <BranchTreeModal
        open={branchTreeModalOpen}
        onClose={handleCloseBranchTree}
        branchTree={branchTree}
        loading={branchTreeLoading}
        activeConversationId={activeConversation?.id}
        onSelectBranch={handleSelectBranch}
        brandVisualTheme={brandVisualTheme}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
